/**
 * Analytics Service - Firebase Analytics wrapper
 * Optimisé pour application de recharge de compte bookmaker (paris sportifs)
 * Équivalent du service analytics_service.dart et analytics_helper.dart dans Flutter
 */

import { getAnalytics, logEvent, setUserId, setUserProperties, type Analytics } from 'firebase/analytics'
import { app } from './firebase'

// Types pour le suivi bookmaker et publicitaire
interface BookmakerOperation {
  bookmaker_name: string
  operation_type: 'deposit' | 'withdrawal'
  amount: number
  currency: string
  payment_method: 'orange' | 'moov' | 'wave' | 'mtn'
  user_account_type: 'premium' | 'standard'
  commercial_id?: string
}

interface AdPerformance {
  ad_unit_id: string
  ad_format: 'banner' | 'interstitial' | 'rewarded' | 'native'
  ad_platform: 'admob' | 'adsense'
  revenue: number
  currency: string
  country_code?: string
  ecpm?: number
  impression_count?: number
  screen_context: 'home' | 'payment' | 'deposit' | 'withdrawal'
}

interface CouponUsage {
  coupon_code: string
  discount_type: 'percentage' | 'fixed_amount'
  discount_value: number
  min_deposit_required?: number
  max_discount?: number
  is_valid: boolean
}

class AnalyticsService {
  private static instance: AnalyticsService
  private analytics: Analytics | null = null
  private initialized = false
  private campaignParams: Record<string, string> = {}
  private inAppBrowser = false
  private standalone = false

  private constructor() {}

  static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService()
    }
    return AnalyticsService.instance
  }

  initialize() {
    try {
      if (typeof window === 'undefined') {
        console.debug('Analytics not available in SSR')
        return
      }

      this.analytics = getAnalytics(app)
      this.initialized = true
      this.captureContext()
      console.debug('🎯 Firebase Analytics initialized')
    } catch (error) {
      console.error('Error initializing Analytics:', error)
    }
  }

  // Capture campaign and browser context once
  private captureContext() {
    if (typeof window === 'undefined') return

    const url = new URL(window.location.href)
    const params = url.searchParams

    this.campaignParams = {
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_content: params.get('utm_content') || '',
      utm_term: params.get('utm_term') || '',
      referrer: document.referrer || '',
      commercial_id: params.get('commercial_id') || params.get('cid') || params.get('ref') || '',
      landing_path: url.pathname,
      entry_url: window.location.href,
    }

    const ua = navigator.userAgent || ''
    const href = window.location.href
    this.inAppBrowser =
      /FBAN|FBAV|Instagram|TikTok|Line|WhatsApp|WeChat/i.test(ua) ||
      /fb_iab|tiktokbrowser|instagram|line|whatsapp|wechat/i.test(ua) ||
      href.includes('fbclid') ||
      href.includes('ttclid')

    this.standalone =
      window.matchMedia?.('(display-mode: standalone)').matches === true ||
      (window.navigator as unknown as { standalone?: boolean }).standalone === true

    // Set default user properties so every event inherits context
    try {
      setUserProperties(this.analytics!, {
        is_in_app_browser: String(this.inAppBrowser),
        is_standalone: String(this.standalone),
        utm_source: this.campaignParams.utm_source,
        utm_medium: this.campaignParams.utm_medium,
        utm_campaign: this.campaignParams.utm_campaign,
        commercial_id: this.campaignParams.commercial_id,
      })
    } catch (error) {
      console.debug('Failed to set default user properties:', error)
    }
  }

  // Helper to safely log event
  private logEventSafely(eventName: string, params?: Record<string, any>) {
    if (!this.initialized || !this.analytics) return

    try {
      const enrichedParams: Record<string, any> = {
        ...this.campaignParams,
        is_in_app_browser: this.inAppBrowser,
        is_standalone: this.standalone,
        session_timestamp: new Date().toISOString(),
        ...params,
      }

      // Firebase Analytics limits param values to 100 chars
      Object.keys(enrichedParams).forEach((key) => {
        const value = enrichedParams[key]
        if (typeof value === 'string' && value.length > 100) {
          enrichedParams[key] = value.slice(0, 100)
        }
      })

      logEvent(this.analytics, eventName as any, enrichedParams)
    } catch (error) {
      console.error(`Error tracking event ${eventName}:`, error)
    }
  }

  setCommercialId(commercialId: string) {
    this.campaignParams = { ...this.campaignParams, commercial_id: commercialId }
  }

  trackScreenView(screenName: string) {
    this.logEventSafely('screen_view', {
      screen_name: screenName,
      timestamp: new Date().toISOString(),
    })
    console.debug(`📊 Screen view tracked: ${screenName}`)
  }

  trackDepositInitiated(params: {
    amount: number
    accountType: string
    commercialId?: string
  }) {
    this.logEventSafely('deposit_initiated', {
      value: params.amount,
      account_type: params.accountType,
      commercial_id: params.commercialId || this.campaignParams.commercial_id || 'none',
      currency: 'XOF',
      timestamp: new Date().toISOString(),
    })
    console.debug('💰 Deposit initiated tracked:', params)
  }

  trackWithdrawalInitiated(params: {
    amount: number
    accountType: string
    commercialId?: string
  }) {
    this.logEventSafely('withdrawal_initiated', {
      value: params.amount,
      account_type: params.accountType,
      commercial_id: params.commercialId || this.campaignParams.commercial_id || 'none',
      currency: 'XOF',
      timestamp: new Date().toISOString(),
    })
    console.debug('💸 Withdrawal initiated tracked:', params)
  }

  trackAccountTypeChanged(accountType: string) {
    this.logEventSafely('account_type_changed', {
      account_type: accountType,
      timestamp: new Date().toISOString(),
    })
    console.debug(`🔄 Account type changed to: ${accountType}`)
  }

  trackAmountSelected(amount: number) {
    this.logEventSafely('amount_selected', {
      value: amount,
      currency: 'XOF',
      timestamp: new Date().toISOString(),
    })
  }

  trackPaymentMethodSelected(method: 'orange' | 'moov') {
    this.logEventSafely('payment_method_selected', {
      method: method,
      timestamp: new Date().toISOString(),
    })
    console.debug(`💳 Payment method selected: ${method}`)
  }

  trackTransactionStatus(params: {
    transactionId: string
    status: 'pending' | 'success' | 'failed'
    amount: number
    type: 'deposit' | 'withdrawal'
  }) {
    this.logEventSafely('transaction_status', {
      transaction_id: params.transactionId,
      status: params.status,
      value: params.amount,
      type: params.type,
      currency: 'XOF',
      timestamp: new Date().toISOString(),
    })
    console.debug('📋 Transaction status tracked:', params)
  }

  trackPWAInstall() {
    this.logEventSafely('pwa_installed', {
      timestamp: new Date().toISOString(),
    })
    console.debug('📱 PWA install tracked')
  }

  trackPWAPromptShown() {
    this.logEventSafely('pwa_prompt_shown', {
      timestamp: new Date().toISOString(),
    })
  }

  trackLogin(method: string = 'email') {
    this.logEventSafely('login', {
      method: method,
      timestamp: new Date().toISOString(),
    })
    console.debug('🔐 Login tracked')
  }

  trackSignUp(method: string = 'email') {
    this.logEventSafely('sign_up', {
      method: method,
      timestamp: new Date().toISOString(),
    })
    console.debug('✨ Sign up tracked')
  }

  setUser(userId: string) {
    if (!this.initialized || !this.analytics) return

    try {
      setUserId(this.analytics, userId)
      console.debug('👤 User ID set:', userId)
    } catch (error) {
      console.error('Error setting user ID:', error)
    }
  }

  setUserProperty(properties: Record<string, any>) {
    if (!this.initialized || !this.analytics) return

    try {
      setUserProperties(this.analytics, properties)
      console.debug('📝 User properties set:', properties)
    } catch (error) {
      console.error('Error setting user properties:', error)
    }
  }

  trackEvent(eventName: string, params?: Record<string, any>) {
    this.logEventSafely(eventName, {
      ...params,
      timestamp: new Date().toISOString(),
    })
    console.debug(`📊 Event tracked: ${eventName}`, params)
  }

  trackError(error: Error, context?: string) {
    this.logEventSafely('error', {
      error_message: error.message,
      error_stack: error.stack,
      context: context || 'unknown',
      timestamp: new Date().toISOString(),
    })
    console.error('❌ Error tracked:', error)
  }

  // === BOOKMAKER SPECIFIC EVENTS ===

  trackBookmakerDeposit(params: BookmakerOperation) {
    this.logEventSafely('bookmaker_deposit', {
      bookmaker_name: params.bookmaker_name,
      payment_method: params.payment_method,
      value: params.amount,
      currency: params.currency,
      user_account_type: params.user_account_type,
      commercial_id: params.commercial_id || 'none',
      timestamp: new Date().toISOString(),
    })
    console.debug('🎰 Bookmaker deposit tracked:', params)
  }

  trackBookmakerWithdrawal(params: BookmakerOperation) {
    this.logEventSafely('bookmaker_withdrawal', {
      bookmaker_name: params.bookmaker_name,
      payment_method: params.payment_method,
      value: params.amount,
      currency: params.currency,
      user_account_type: params.user_account_type,
      commercial_id: params.commercial_id || 'none',
      timestamp: new Date().toISOString(),
    })
    console.debug('💰 Bookmaker withdrawal tracked:', params)
  }

  trackCouponUsed(params: CouponUsage) {
    this.logEventSafely('coupon_used', {
      coupon_code: params.coupon_code,
      discount_type: params.discount_type,
      discount_value: params.discount_value,
      min_deposit_required: params.min_deposit_required || 0,
      max_discount: params.max_discount || 0,
      is_valid: params.is_valid,
      timestamp: new Date().toISOString(),
    })
    console.debug('🎫 Coupon usage tracked:', params)
  }

  trackCouponGenerated(params: { coupon_code: string; discount_value: number; affiliate_code?: string }) {
    this.logEventSafely('coupon_generated', {
      coupon_code: params.coupon_code,
      discount_value: params.discount_value,
      affiliate_code: params.affiliate_code || 'none',
      timestamp: new Date().toISOString(),
    })
    console.debug('🎟️ Coupon generated tracked:', params)
  }

  trackAffiliateSignup(params: { affiliate_code: string; referred_user_id?: string }) {
    this.logEventSafely('affiliate_signup', {
      affiliate_code: params.affiliate_code,
      referred_user_id: params.referred_user_id || 'none',
      timestamp: new Date().toISOString(),
    })
    console.debug('🤝 Affiliate signup tracked:', params)
  }

  trackBookmakerSelected(bookmakerName: string, fromScreen: string) {
    this.logEventSafely('bookmaker_selected', {
      bookmaker_name: bookmakerName,
      from_screen: fromScreen,
      timestamp: new Date().toISOString(),
    })
    console.debug(`📱 Bookmaker selected: ${bookmakerName} from ${fromScreen}`)
  }

  // === ADVERTISING PERFORMANCE EVENTS ===

  trackAdImpression(params: AdPerformance) {
    this.logEventSafely('ad_impression', {
      ad_unit_id: params.ad_unit_id,
      ad_format: params.ad_format,
      ad_platform: params.ad_platform,
      screen_context: params.screen_context,
      country_code: params.country_code || 'unknown',
      timestamp: new Date().toISOString(),
    })
    console.debug('📢 Ad impression tracked:', params)
  }

  trackAdClicked(params: {
    ad_unit_id: string
    ad_format: string
    ad_platform: string
    screen_context: string
    destination_url?: string
  }) {
    this.logEventSafely('ad_clicked', {
      ad_unit_id: params.ad_unit_id,
      ad_format: params.ad_format,
      ad_platform: params.ad_platform,
      screen_context: params.screen_context,
      destination_url: params.destination_url || 'none',
      timestamp: new Date().toISOString(),
    })
    console.debug('👆 Ad clicked tracked:', params)
  }

  trackAdRevenue(params: AdPerformance) {
    this.logEventSafely('ad_revenue', {
      ad_unit_id: params.ad_unit_id,
      ad_format: params.ad_format,
      ad_platform: params.ad_platform,
      value: params.revenue,
      currency: params.currency,
      ecpm: params.ecpm || 0,
      impression_count: params.impression_count || 1,
      screen_context: params.screen_context,
      country_code: params.country_code || 'unknown',
      timestamp: new Date().toISOString(),
    })
    console.debug('💵 Ad revenue tracked:', params)
  }

  trackAdLoaded(params: {
    ad_unit_id: string
    ad_format: string
    ad_platform: string
    screen_context: string
    load_success: boolean
    error_code?: string
  }) {
    this.logEventSafely('ad_loaded', {
      ad_unit_id: params.ad_unit_id,
      ad_format: params.ad_format,
      ad_platform: params.ad_platform,
      screen_context: params.screen_context,
      load_success: params.load_success,
      error_code: params.error_code || 'none',
      timestamp: new Date().toISOString(),
    })
    console.debug('📦 Ad load tracked:', params)
  }

  // === CONVERSION FUNNEL EVENTS ===

  trackFunnelStep(params: {
    funnel_name: string
    step_name: string
    step_number: number
    total_steps: number
    user_properties?: Record<string, any>
  }) {
    this.logEventSafely('funnel_step', {
      funnel_name: params.funnel_name,
      step_name: params.step_name,
      step_number: params.step_number,
      total_steps: params.total_steps,
      completion_rate: (params.step_number / params.total_steps) * 100,
      timestamp: new Date().toISOString(),
      ...params.user_properties,
    })
    console.debug(`🔝 Funnel step tracked: ${params.funnel_name} - ${params.step_name}`)
  }

  trackConversionCompleted(params: {
    conversion_type: 'first_deposit' | 'repeat_deposit' | 'withdrawal' | 'affiliate_signup'
    conversion_value: number
    currency: string
    conversion_time_ms?: number
    user_journey?: string[]
  }) {
    this.logEventSafely('conversion_completed', {
      conversion_type: params.conversion_type,
      value: params.conversion_value,
      currency: params.currency,
      conversion_time_ms: params.conversion_time_ms || 0,
      user_journey: params.user_journey?.join(',') || 'none',
      timestamp: new Date().toISOString(),
    })
    console.debug('🎯 Conversion completed tracked:', params)
  }

  // === USER ENGAGEMENT EVENTS ===

  trackUserEngagement(params: {
    engagement_type: 'session_duration' | 'screen_time' | 'feature_usage'
    engagement_value: number
    feature_name?: string
    session_duration_ms?: number
  }) {
    this.logEventSafely('user_engagement', {
      engagement_type: params.engagement_type,
      engagement_value: params.engagement_value,
      feature_name: params.feature_name || 'none',
      session_duration_ms: params.session_duration_ms || 0,
      timestamp: new Date().toISOString(),
    })
    console.debug('📱 User engagement tracked:', params)
  }

  trackRetentionEvent(params: {
    day_number: number
    is_active: boolean
    last_session_days_ago: number
    total_sessions_count: number
  }) {
    this.logEventSafely('retention_event', {
      day_number: params.day_number,
      is_active: params.is_active,
      last_session_days_ago: params.last_session_days_ago,
      total_sessions_count: params.total_sessions_count,
      timestamp: new Date().toISOString(),
    })
    console.debug(`🔄 Day ${params.day_number} retention tracked:`, params.is_active)
  }

  // === REVENUE TRACKING ===

  trackEcommercePurchase(params: {
    transaction_id: string
    value: number
    currency: string
    items: Array<{
      item_id: string
      item_name: string
      category: string
      price: number
      quantity: number
    }>
  }) {
    this.logEventSafely('purchase', {
      transaction_id: params.transaction_id,
      value: params.value,
      currency: params.currency,
      items: params.items,
      timestamp: new Date().toISOString(),
    })
    console.debug('💳 E-commerce purchase tracked:', params)
  }
}

export const analyticsService = AnalyticsService.getInstance()
